import { useState, useEffect } from 'react';
import apiClient from '../lib/api';
import type { Log, LogLevel } from '../types';
import { RefreshCw, ChevronDown, ChevronUp, FileText } from 'lucide-react';

export default function LogsViewer() {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<LogLevel | 'ALL'>('ALL');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchLogs();
  }, [levelFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: { level?: string; limit: number } = {
        limit: 50,
      };

      if (levelFilter !== 'ALL') {
        params.level = levelFilter;
      }

      const { data } = await apiClient.get<Log[]>('/api/logs', { params });

      setLogs(data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch logs');
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (logId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getLevelBadgeClass = (level: LogLevel) => {
    switch (level) {
      case 'ERROR':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'WARN':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'INFO':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatMetadata = (metadata: Record<string, any> | null) => {
    if (!metadata) return 'אין מטא-דאטה';
    return JSON.stringify(metadata, null, 2);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading logs...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl w-full">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">לוגים של המערכת</h1>
        <p className="text-sm sm:text-base text-gray-600">מעקב אחר פעילות ואירועי מערכת</p>
      </div>

      {/* Filters and Actions */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <label htmlFor="levelFilter" className="text-sm font-medium text-gray-700">
            סנן לפי רמה:
          </label>
          <select
            id="levelFilter"
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value as LogLevel | 'ALL')}
            className="px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent min-h-[44px]"
          >
            <option value="ALL">הכל</option>
            <option value="ERROR">שגיאה</option>
            <option value="WARN">אזהרה</option>
            <option value="INFO">מידע</option>
          </select>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-black text-white rounded-md font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] w-full sm:w-auto"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>רענן</span>
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Mobile Card Layout */}
      <div className="block md:hidden space-y-4">
        {logs.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            לא נמצאו לוגים
          </div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getLevelBadgeClass(
                        log.level
                      )}`}
                    >
                      {log.level}
                    </span>
                    <span className="text-xs text-gray-500 truncate">{log.event_type}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{formatTimestamp(log.created_at)}</p>
                  <p className="text-sm text-gray-900 break-words">{log.message}</p>
                </div>
              </div>
              <button
                onClick={() => toggleRow(log.id)}
                className="w-full mt-3 flex items-center justify-center space-x-1 text-gray-600 hover:text-gray-900 transition-colors py-2 rounded-md hover:bg-gray-50 min-h-[44px]"
              >
                {expandedRows.has(log.id) ? (
                  <>
                    <ChevronUp className="w-4 h-4" />
                    <span className="text-sm font-medium">הסתר פרטים</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    <span className="text-sm font-medium">הצג פרטים</span>
                  </>
                )}
              </button>
              {expandedRows.has(log.id) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">מטא-דאטה:</span>
                    </div>
                    <pre className="bg-gray-50 border border-gray-200 rounded-md p-3 text-xs overflow-x-auto">
                      {formatMetadata(log.metadata)}
                    </pre>
                    {log.message && (
                      <div>
                        <span className="text-sm font-medium text-gray-700">הודעה מלאה:</span>
                        <p className="mt-1 text-sm text-gray-900 break-words">{log.message}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:block bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  זמן
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  רמה
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  סוג אירוע
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  הודעה
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    לא נמצאו לוגים
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <>
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatTimestamp(log.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getLevelBadgeClass(
                            log.level
                          )}`}
                        >
                          {log.level}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.event_type}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-md truncate">
                        {log.message}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => toggleRow(log.id)}
                          className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 transition-colors min-h-[44px] px-2"
                        >
                          {expandedRows.has(log.id) ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              <span>הסתר</span>
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              <span>הצג</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedRows.has(log.id) && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-gray-50">
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2 mb-2">
                              <FileText className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-700">מטא-דאטה:</span>
                            </div>
                            <pre className="bg-white border border-gray-200 rounded-md p-4 text-xs overflow-x-auto">
                              {formatMetadata(log.metadata)}
                            </pre>
                            {log.message && (
                              <div className="mt-2">
                                <span className="text-sm font-medium text-gray-700">הודעה מלאה:</span>
                                <p className="mt-1 text-sm text-gray-900">{log.message}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {logs.length > 0 && (
        <div className="mt-4 text-xs sm:text-sm text-gray-500">
          מוצגים {logs.length} לוג{logs.length !== 1 ? 'ים' : ''}
        </div>
      )}
    </div>
  );
}
